import _ from 'lodash';
import {
  getDataWithRetry,
  mapPagedResult,
  sendWithoutBody,
} from '../fetchUtils';
import { getDefaultError } from '../../utils';
import {
  isValidExercisesResponse,
  isValidExerciseResponse,
  isValidExerciseListResponse,
} from '../apiValidators';
import { apiBaseUrl } from '../apiConsts';
import { buildPaginationQuery, MAX_PAGE_SIZE } from '../pagination';
import { isRecord } from '../../utils/typeUtils';
import { AutomaticMetricsType } from '../../constants';

import type {
  Exercise,
  WithOptionalId,
  PagedResponse,
  PaginatedListParams,
  ExerciseListItemDto,
} from '../../types';
import type { FileResponse } from '../apiTypes';
import type { FetchResult, FetchSuccess } from '../fetchUtils';
type UploadResponse = Record<string, string>;
type SourceExercisesPageHandler = (exercises: Exercise[]) => void;

const yieldToBrowser = (): Promise<void> =>
  new Promise((resolve) => {
    window.setTimeout(resolve, 0);
  });

const createSuccessResult = <T>(data: T): FetchSuccess<T> => ({
  success: true,
  data,
  status: 200,
  durationMs: 0,
});

const isUploadResponse = (data: unknown): data is UploadResponse => {
  return (
    isRecord(data) &&
    Object.values(data).every((value) => typeof value === 'string')
  );
};

export const fetchExerciseById = async (
  id: string,
): Promise<FetchResult<Exercise | null>> => {
  return getDataWithRetry<Exercise | null>(
    `${apiBaseUrl}exercises/${id}`,
    {},
    isValidExerciseResponse,
  );
};

export const getSourceExercises = async (
  trainerId?: string,
  onPageLoaded?: SourceExercisesPageHandler,
): Promise<FetchResult<Exercise[]>> => {
  const createSourceExercisesUrl = (page: number) =>
    trainerId
      ? `${apiBaseUrl}exercises/trainer/merged/${trainerId}?page=${page}&size=${MAX_PAGE_SIZE}&sort=name`
      : `${apiBaseUrl}exercises?page=${page}&size=${MAX_PAGE_SIZE}&sort=name`;

  const firstResponse = await getDataWithRetry<
    PagedResponse<ExerciseListItemDto>
  >(createSourceExercisesUrl(0), {}, isValidExerciseListResponse);

  if (!firstResponse.success) {
    return firstResponse;
  }

  const firstPageExercises = (firstResponse.data.content ?? []).map(
    mapExerciseListItemToExercise,
  );
  onPageLoaded?.(firstPageExercises);

  const totalPages = firstResponse.data.meta?.totalPages ?? 1;

  if (totalPages > 1 && onPageLoaded) {
    await yieldToBrowser();
  }

  if (totalPages <= 1) {
    return {
      ...firstResponse,
      data: firstPageExercises,
    };
  }

  const remainingPages = await Promise.all(
    Array.from({ length: totalPages - 1 }, async (_, i) => {
      const response = await getDataWithRetry<
        PagedResponse<ExerciseListItemDto>
      >(createSourceExercisesUrl(i + 1), {}, isValidExerciseListResponse);

      if (!response.success) {
        return response;
      }

      const pageExercises = (response.data.content ?? []).map(
        mapExerciseListItemToExercise,
      );
      onPageLoaded?.(pageExercises);
      return {
        ...response,
        data: pageExercises,
      };
    }),
  );

  const failed = remainingPages.find((r) => !r.success);
  if (failed && !failed.success) return failed;

  const allExercises = [
    ...firstPageExercises,
    ...remainingPages.flatMap((r) => (r.success ? r.data : [])),
  ];

  return {
    ...firstResponse,
    data: allExercises,
  };
};

// NOTE: Manager dashboards still load each trainer's authored personalized
// exercises separately. A logged-in trainer's own library comes from the merged
// source endpoint in getSourceExercises.
export const getTrainerExercises = async (
  trainerIds: string[],
): Promise<FetchResult<Exercise[]>> => {
  if (!trainerIds || !trainerIds.length) {
    return createSuccessResult<Exercise[]>([]);
  }
  const responses = await Promise.all(
    trainerIds.map((trainerId) =>
      getDataWithRetry<PagedResponse<Exercise>>(
        `${apiBaseUrl}exercises/trainer/${trainerId}?page=0&size=${MAX_PAGE_SIZE}&sort=name`,
        {},
        isValidExercisesResponse,
      ),
    ),
  );
  const failure = responses.find((response) => !response.success);
  if (failure && !failure.success) {
    return {
      success: false,
      data: null,
      error:
        failure.error || getDefaultError('Failed to fetch trainer exercises'),
      status: failure.status,
      durationMs: failure.durationMs,
    };
  }
  const successfulResponses = responses as FetchSuccess<
    PagedResponse<Exercise>
  >[];
  const mergedExercises = successfulResponses
    .flatMap((response) => response.data.content ?? [])
    .filter(Boolean);
  return createSuccessResult<Exercise[]>(mergedExercises);
};

const mapExerciseListItemToExercise = (dto: ExerciseListItemDto): Exercise => ({
  id: dto.id,
  name: dto.name ?? '',
  videoUrl: dto.videoUrl ?? '',
  imageUrl: dto.imageUrl ?? '',
  movementPattern: dto.movementPattern ?? '',
  modality: dto.modality ?? '',
  planeOfMotion: '',
  isWeightsPerSide: dto.isWeightsPerSide ?? false,
  isUnilateral: dto.isUnilateral ?? false,
  isPersonalized: dto.isPersonalized ?? undefined,
  isDeleted: dto.isDeleted ?? undefined,
  trainerId: dto.trainerId ?? undefined,
  originalExerciseId: dto.originalExerciseId ?? undefined,
  primaryMuscleNames: dto.primaryMuscleGroupNames,
  primaryMuscleGroupIds: [],
  secondaryMuscleGroupIds: [],
  preferredStationIds: [],
  analysisOptions: dto.canBeAutomatic
    ? {
        supported: true,
        defaultMetrics: {
          concentricType: AutomaticMetricsType.Tempo,
          eccentricType: AutomaticMetricsType.Tempo,
          velocity: {
            concentric: { min: 0, max: 0 },
            eccentric: { min: 0, max: 0 },
          },
          tempo: {
            concentric: { min: 0, max: 0 },
            eccentric: { min: 0, max: 0 },
          },
        },
      }
    : undefined,
});

export const fetchMergedExercisesPageByTrainerId = async (
  trainerId: string,
  params: PaginatedListParams,
): Promise<FetchResult<PagedResponse<Exercise>>> => {
  const result = await getDataWithRetry<PagedResponse<ExerciseListItemDto>>(
    `${apiBaseUrl}exercises/trainer/merged/${trainerId}?${buildPaginationQuery(params)}`,
    {},
    isValidExerciseListResponse,
  );

  if (!result.success) {
    return result;
  }

  return {
    ...result,
    data: {
      ...result.data,
      content: (result.data.content ?? []).map(mapExerciseListItemToExercise),
    },
  };
};

const mutateExerciseMuscle = async (
  exerciseId: string,
  muscleGroupType: string,
  action: 'associate' | 'unassociate',
  ids: string[],
): Promise<FetchResult<null>> => {
  if (!ids.length) {
    return createSuccessResult<null>(null);
  }
  return sendWithoutBody(
    `${apiBaseUrl}exercises/${exerciseId}/${action}/${muscleGroupType}`,
    {
      headers: { 'content-type': 'application/json' },
      method: 'POST',
      body: JSON.stringify(ids),
    },
  );
};

export const updateExerciseData = async (
  oldExerciseData: Exercise,
  newExerciseData: Exercise,
): Promise<FetchResult<Exercise | null>> => {
  if (!newExerciseData?.id) {
    return {
      data: null,
      success: false,
      error: getDefaultError('No exercise id provided'),
    };
  }

  const updateResponse = await getDataWithRetry<Exercise | null>(
    `${apiBaseUrl}exercises`,
    {
      headers: { 'content-type': 'application/json' },
      method: 'PUT',
      body: JSON.stringify(newExerciseData),
    },
    isValidExerciseResponse,
  );
  if (!updateResponse.success || !updateResponse.data) {
    return updateResponse;
  }

  const primaryMuscleIdsToUnassociate = _.difference(
    oldExerciseData.primaryMuscleGroupIds,
    newExerciseData.primaryMuscleGroupIds,
  );
  const primaryMuscleIdsToAssociate = _.difference(
    newExerciseData.primaryMuscleGroupIds,
    oldExerciseData.primaryMuscleGroupIds,
  );
  const secondaryMuscleIdsToUnassociate = _.difference(
    oldExerciseData.secondaryMuscleGroupIds,
    newExerciseData.secondaryMuscleGroupIds,
  );
  const secondaryMuscleIdsToAssociate = _.difference(
    newExerciseData.secondaryMuscleGroupIds,
    oldExerciseData.secondaryMuscleGroupIds,
  );
  const associationSteps: Array<Promise<FetchResult<null>>> = [
    mutateExerciseMuscle(
      newExerciseData.id,
      'primary-muscles',
      'unassociate',
      primaryMuscleIdsToUnassociate,
    ),
    mutateExerciseMuscle(
      newExerciseData.id,
      'primary-muscles',
      'associate',
      primaryMuscleIdsToAssociate,
    ),
    mutateExerciseMuscle(
      newExerciseData.id,
      'secondary-muscles',
      'unassociate',
      secondaryMuscleIdsToUnassociate,
    ),
    mutateExerciseMuscle(
      newExerciseData.id,
      'secondary-muscles',
      'associate',
      secondaryMuscleIdsToAssociate,
    ),
  ];

  for (const step of associationSteps) {
    const result = await step;
    if (!result.success) {
      return result as FetchResult<Exercise | null>;
    }
  }
  return updateResponse;
};

export const createPersonalizedExercise = async (
  exercise: WithOptionalId<Exercise>,
): Promise<FetchResult<Exercise | null>> => {
  if (exercise.id) {
    return {
      data: null,
      success: false,
      error: getDefaultError('cannot create an exercise with id'),
    };
  }

  return getDataWithRetry(
    `${apiBaseUrl}exercises/personalized`,
    {
      headers: { 'content-type': 'application/json' },
      method: 'POST',
      body: JSON.stringify(exercise),
    },
    isValidExerciseResponse,
  );
};

export const createGlobalExercise = async (
  exercise: WithOptionalId<Exercise>,
): Promise<FetchResult<Exercise | null>> => {
  if (exercise.id) {
    return {
      data: null,
      success: false,
      error: getDefaultError('cannot create an exercise with id'),
    };
  }
  return getDataWithRetry(
    `${apiBaseUrl}exercises`,
    {
      headers: { 'content-type': 'application/json' },
      method: 'POST',
      body: JSON.stringify(exercise),
    },
    isValidExerciseResponse,
  );
};

export const deleteExercise = async (
  exerciseId: string,
): Promise<FetchResult<null>> => {
  if (!exerciseId) {
    return {
      data: null,
      success: false,
      error: getDefaultError('exerciseId is required'),
    };
  }
  return sendWithoutBody(`${apiBaseUrl}exercises/${exerciseId}/remove`, {
    method: 'PUT',
  });
};

export const uploadExerciseImages = async (
  files: File[],
  trainerId: string,
): Promise<FetchResult<FileResponse[]>> => {
  if (!files?.length || !trainerId) {
    return {
      data: null,
      success: false,
      error: getDefaultError('wrong parameters in uploadImage'),
    };
  }
  const filesFormData = new FormData();
  files.forEach((file) => {
    filesFormData.append('files', file);
  });
  const response = await getDataWithRetry<UploadResponse>(
    `${apiBaseUrl}exercises/uploadBulkImages?trainerId=${trainerId}&compress=true`,
    {
      mode: 'cors',
      method: 'POST',
      body: filesFormData,
    },
    isUploadResponse,
    2,
    120000, // 20 minutes
  );
  if (!response.success || !response.data) {
    return {
      success: false,
      data: null,
      error:
        response.error || getDefaultError('Failed to upload exercise images'),
      status: response.status,
      durationMs: response.durationMs,
    };
  }
  const responseData = Object.entries(response.data)
    .map(([fileName, fileUrl]) => {
      if (!fileName || !fileUrl) {
        return null;
      }
      return {
        fileName,
        fileUrl,
      };
    })
    .filter((item): item is FileResponse => item !== null);
  return createSuccessResult<FileResponse[]>(responseData);
};

export const uploadExerciseVideos = async (
  files: File[],
  trainerId: string,
): Promise<FetchResult<FileResponse[]>> => {
  if (!files?.length || !trainerId) {
    return {
      data: null,
      success: false,
      error: getDefaultError('wrong parameters in uploadExerciseVideos'),
    };
  }
  const filesFormData = new FormData();
  files.forEach((file) => {
    filesFormData.append('files', file);
  });
  const response = await getDataWithRetry<UploadResponse>(
    `${apiBaseUrl}exercises/uploadBulkVideos?trainerId=${trainerId}&compress=true`,
    {
      mode: 'cors',
      method: 'POST',
      body: filesFormData,
    },
    isUploadResponse,
    2,
    120000, // 20 minutes
  );
  if (!response.success || !response.data) {
    return {
      success: false,
      data: null,
      error:
        response.error || getDefaultError('Failed to upload exercise videos'),
      status: response.status,
      durationMs: response.durationMs,
    };
  }
  const responseData = Object.entries(response.data)
    .map(([fileName, fileUrl]) => {
      if (!fileName || !fileUrl) {
        return null;
      }
      return {
        fileName,
        fileUrl,
      };
    })
    .filter((item): item is FileResponse => item !== null);
  return createSuccessResult<FileResponse[]>(responseData);
};

export const fetchExercisesByIds = async (
  ids: string[],
): Promise<FetchResult<Exercise[]>> => {
  if (!ids.length) {
    return createSuccessResult<Exercise[]>([]);
  }
  const response = await getDataWithRetry<PagedResponse<Exercise>>(
    `${apiBaseUrl}exercises/getByIds?page=0&size=${ids.length}`,
    {
      headers: { 'content-type': 'application/json' },
      method: 'POST',
      body: JSON.stringify(ids),
    },
    isValidExercisesResponse,
  );
  return mapPagedResult(response);
};
